import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SelectOptionI } from '../../../../../../shared/modules/configurable-form/components/form-select/models/select-option-i';
import { SelectOptionLoaderI } from '../../../../../../shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { OrderBuilderState } from '../../../../../reducers';
import { getUnloadTypes } from '../../../selectors/single-catalogs';
import { select, Store } from '@ngrx/store';
import { BOOM_PUMP, PUMP, STATIONARY_PUMP } from '../../../constants/pumps';
import { PumpTypes } from './pumpable-unload-type.loader.service';
import { hasPumpAndHasDifferentToPumpRMXUL } from '../../../helpers/should-hide-pump';

@Injectable()
export class UniversalLinkUnloadTypesLoaderService implements SelectOptionLoaderI {
  constructor(protected store: Store<OrderBuilderState>) {}

  /**
   * Loads and returns an observable of unload type options.
   * Combines the result of checking pump conditions with the list of unload types.
   * Filters and sorts the unload types based on specific conditions:
   * - If the unload type is a pump, it ensures that either BOOM_PUMP or STATIONARY_PUMP is not present.
   * - If the form has a different pump type and is pumpable, it returns all options.
   * - If the form has a different pump type and is not pumpable, it filters out pump types.
   * - If the form does not have a different pump type, it filters to include only pump types.
   * @param {FormGroup} formGroup - The form group to check for pump conditions.
   * @return {Observable<SelectOptionI[]>} - An observable emitting the list of unload type options.
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return combineLatest([
      this.hasPumpAndHasDifferentToPump(formGroup),
      this.store.pipe(
        select(getUnloadTypes),
        filter((state) => !!state),
        map((unloadTypes) => {
          if (unloadTypes.length) {
            let sortedUnloadTypes = unloadTypes.sort((a, b) => a.unloadTypeDesc.localeCompare(b.unloadTypeDesc));

            sortedUnloadTypes = sortedUnloadTypes.filter((type) => {
              if (type.unloadTypeCode === PUMP) {
                const hasBoomPump = sortedUnloadTypes.some((t) => t.unloadTypeCode === BOOM_PUMP);
                const hasStationaryPump = sortedUnloadTypes.some((t) => t.unloadTypeCode === STATIONARY_PUMP);
                return !hasBoomPump || !hasStationaryPump;
              }
              return true;
            });

            const mappedUnloadTypes = sortedUnloadTypes.map((unloadType) => {
              return {
                id: unloadType.unloadTypeCode,
                name: `${unloadType.unloadTypeDesc}`,
              };
            });
            return mappedUnloadTypes;
          }
          return [];
        }),
      ),
    ]).pipe(
      map(([result, options]) => {
        if (!result.isPumpable) {
          const filtered = options.filter((option) => {
            return !PumpTypes.some((pumpType) => pumpType.id === option.id);
          });
          return { options: filtered, pumpable: result.isPumpable };
        } else {
          return { options, pumpable: result.isPumpable };
        }
      }),
      map(({ options }) => {
        return options;
      }),
    );
  }

  private hasPumpAndHasDifferentToPump(formGroup: UntypedFormGroup) {
    return hasPumpAndHasDifferentToPumpRMXUL(this.store, formGroup);
  }
}
